'use client';

import { useState, useEffect, useRef } from 'react';

import { Box, Group, ThemeIcon, Card, Text } from '@mantine/core';
import { IoChatbubbleOutline } from 'react-icons/io5';

import cx from './AnimatedExamplesBox.module.scss';

interface AnimatedExamplesBoxProps {
    examples: string[];
    minHeight?: number;
    replay?: boolean;
}

const STACK_TIME = 200;
const WAIT_TIME = 4000;

const AnimatedExamplesBox = ({ examples, minHeight = 400, replay }: AnimatedExamplesBoxProps) => {
    const [visibleExamples, setVisibleExamples] = useState<number[]>([]);
    const [iteration, setIteration] = useState(0);
    const visibleRef = useRef<number[]>([]);

    useEffect(() => {
        visibleRef.current = [];
        setVisibleExamples([]);

        const timers: NodeJS.Timeout[] = examples.map((_, index) => {
            return setTimeout(() => {
                visibleRef.current = [...visibleRef.current, index];
                setVisibleExamples([...visibleRef.current]);
            }, index * STACK_TIME);
        });

        let replayTimer: NodeJS.Timeout | null = null;

        if (replay) {
            const totalDuration = examples.length * STACK_TIME + WAIT_TIME;

            replayTimer = setTimeout(() => {
                setIteration((prev) => prev + 1);
            }, totalDuration);
        }

        return () => {
            timers.forEach((timer) => clearTimeout(timer));

            if (replayTimer) {
                clearTimeout(replayTimer);
            }
        };
    }, [examples, replay, iteration]);

    return (
        <Box pos="relative" mih={minHeight} className={`${cx.container} gradient-cyan-green-background-light`}>
            {examples.map((example, index) => (
                <AnimatedExample key={example} visible={visibleExamples.includes(index)} index={index}>
                    {example}
                </AnimatedExample>
            ))}
        </Box>
    );
};

const getRandomPosition = (index: number) => {
    const positions = [
        { top: '5%', left: '3%' },
        { top: '24%', left: '51%' },
        { top: '13%', left: '19%' },
        { top: '82%', left: '5%' },
        { top: '28%', left: '9%' },
        { top: '55%', left: '50%' },
        { top: '65%', left: '15%' },
        { top: '40%', left: '56%' },
        { top: '75%', left: '30%' },
        { top: '5%', left: '58%' },
        { top: '45%', left: '2%' },
        { top: '82%', left: '60%' },
        { top: '35%', left: '23%' },
    ];
    return positions[index % positions.length];
};

const AnimatedExample = ({
    children,
    visible,
    index,
}: {
    children: React.ReactNode;
    visible: boolean;
    index: number;
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const position = getRandomPosition(index);

    return (
        <Box
            className={cx.example}
            style={{
                ...position,

                zIndex: isHovered ? 2 : 1,

                opacity: visible ? 1 : 0,

                maxWidth: '40%',

                transform: visible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(20px)',
                transition: 'all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',

                willChange: 'transform, opacity, z-index',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Card
                withBorder
                py="xs"
                px="sm"
                shadow={isHovered ? 'xl' : 'md'}
                style={{
                    cursor: 'default',

                    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 200ms ease',

                    willChange: 'transform, box-shadow',
                }}
            >
                <Group gap="xs">
                    <ThemeIcon variant="light" size="md" radius={99} color="cyan">
                        <IoChatbubbleOutline size={14} />
                    </ThemeIcon>
                    <Text fz="sm" style={{ flex: 1 }}>
                        {children}
                    </Text>
                </Group>
            </Card>
        </Box>
    );
};

export { AnimatedExamplesBox };
